import React from 'react';
import { Card } from '../../ui/Card';
import { useWeight } from '../../../context/WeightContext';
import { BodyMeasurement } from '../../../context/WeightContext';
import { WeightMetrics } from './WeightMetrics';

export function BodyMeasurements() {
  const { measurements, formatMeasurement } = useWeight();
  const [view, setView] = React.useState<'dashboard' | 'history' | 'add'>('dashboard');
  const [selectedEntry, setSelectedEntry] = React.useState<BodyMeasurement | null>(null);

  const lastMeasurement = measurements[0];
  const lastMeasurementDate = lastMeasurement?.recorded_at
    ? new Date(lastMeasurement.recorded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : 'N/A';

  // Handle entry click to view details
  const handleEntryClick = (entry: BodyMeasurement) => {
    setSelectedEntry(entry);
    setView('dashboard');
  };

  // Format measurement value
  const formatMeasurementValue = (cm: number | null | undefined) => {
    const val = formatMeasurement(cm);
    if (val === null || val === undefined) return '—';
    return val.toFixed(1);
  };

  // Fields configuration
  const fields = [
    { key: 'neck_cm', label: 'Neck' },
    { key: 'chest_cm', label: 'Chest' },
    { key: 'waist_cm', label: 'Waist' },
    { key: 'hips_cm', label: 'Hips' },
    { key: 'upper_arms_cm', label: 'Arms' },
    { key: 'thighs_cm', label: 'Thighs' }
  ];

  // Get current measurement values (from last or selected)
  const currentMeasurement = selectedEntry || lastMeasurement;

  return (
    <div className="flex h-full flex-col p-0">
      {/* Dashboard Section */}
      <div className="p-4 pb-0">
        <h2 className="text-xl font-bold text-on-surface mb-4">Body Measurements</h2>

        <Card className="bg-surface-container border border-outline mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-on-surface-variant">Last measurement: {lastMeasurementDate}</span>
            {measurements.length > 1 && (
              <button
                onClick={() => setView('history')}
                className="text-xs text-primary hover:underline"
              >
                View all history
              </button>
            )}
          </div>

          {currentMeasurement ? (
            <div className="grid grid-cols-2 gap-3">
              {fields.map((field) => (
                <div key={field.key} className="flex flex-col">
                  <span className="text-[10px] text-on-surface-variant">{field.label}</span>
                  <span className="text-lg font-bold text-primary">{formatMeasurementValue(currentMeasurement[field.key as keyof BodyMeasurement])}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-on-surface-variant">
              <p>No measurements yet. Add your first entry below!</p>
            </div>
          )}
        </Card>

        {/* Add Entry Button */}
        <button
          onClick={() => setView('add')}
          className="w-full rounded-xl bg-primary px-4 py-3 font-medium text-primary-contrast shadow-lg shadow-primary/30 active:scale-95 transition-transform"
        >
          + Add Entry
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {view === 'dashboard' && lastMeasurement && (
          <div className="p-4">
            <h3 className="text-base font-semibold text-on-surface mb-3">Measurement Details</h3>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-surface-container border border-outline">
                <p className="text-xs text-on-surface-variant">Recorded on: {lastMeasurement.recorded_at ? new Date(lastMeasurement.recorded_at).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Date unknown'}</p>
                {lastMeasurement.notes && (
                  <p className="text-xs text-on-surface-variant mt-1">Notes: {lastMeasurement.notes}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {view === 'history' && (
          <div className="flex flex-col overflow-hidden">
            <div className="flex-none p-3 border-b border-outline">
              <h3 className="text-base font-semibold text-on-surface">History</h3>
              <p className="text-xs text-on-surface-variant">Click on an entry to view details</p>
            </div>
            <div className="flex-1 overflow-auto">
              {measurements.length === 0 ? (
                <div className="p-8 text-center text-on-surface-variant">
                  <p>No measurement history yet.</p>
                </div>
              ) : (
                measurements.map((entry) => (
                  <Card
                    key={entry.id}
                    onClick={() => handleEntryClick(entry)}
                    className="bg-surface-container border border-outline px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-surface-container-high transition-colors"
                  >
                    <div>
                      <span className="block text-sm font-medium text-on-surface">
                        {entry.recorded_at ? new Date(entry.recorded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'Unknown date'}
                      </span>
                      <span className="text-xs text-on-surface-variant">Tap to view details</span>
                    </div>
                    <span className="text-xs text-on-surface-variant">→</span>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {view === 'add' && (
          <div className="flex flex-col overflow-hidden">
            <div className="flex-none border-b border-outline bg-surface">
              <div className="flex items-center justify-between p-3">
                <h3 className="text-base font-semibold text-on-surface">Add Measurement</h3>
                <button
                  onClick={() => setView('dashboard')}
                  className="text-xs text-primary hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <WeightMetrics />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
